/**
 * Ручной исторический (вне заказа) ввод результата СОУТ по рабочему месту
 * (p2-manual-historical, §11.2). Роль-агностично: клиент и команда используют один и тот
 * же компонент (правка/удаление доступны обоим, §11.2 сценарии). Дата истечения —
 * READ-ONLY вычисляемое поле (design D3, зеркало `historicalValidUntilPreview`) — НЕ
 * поле ввода. Документ — загрузка доступна только команде (`canUploadDocument`, D7: у
 * клиента пока нет client-facing upload-эндпоинта; поле в любом случае необязательно).
 * Точки входа: карточка сотрудника (§1.1) и карточка рабочего места (§3,
 * p3-structure-sout-training design D5, `openLabel`/`closeLabel` — переименование кнопки
 * под текст ТЗ без смены поведения) + командная секция заказа СОУТ (4.4 tasks.md,
 * p2-manual-historical). Поле «Номер рабочего места по прошлой СОУТ» (`rmNumber`,
 * design D5, §8 ТЗ p3-sout-workplaces) — необязательное, маппится на готовый DTO-контракт.
 * Строка срока действия (centrika-docs!29): «Действует до» + чекбокс «Декларация» рядом —
 * бессрочный срок теперь ТРЕБУЕТ отмеченной декларации у класса «1»/«2», сам по себе класс
 * бессрочности больше не даёт; для «3.1»…«4» чекбокс недоступен и не выбран.
 */
import { useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { soutApi, teamApi, type SoutHistoricalResult } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  historicalDeclarationAllowed,
  historicalDeclarationChecked,
  historicalValidUntilPreview,
} from '@/lib/sout'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { FileInput } from '@/design-system/components/primitives/file-input'
import { Alert } from '@/design-system/components/feedback/alert'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const HAZARD_CLASSES = ['1', '2', '3.1', '3.2', '3.3', '3.4', '4']

/** Тип документа «Отчёты СОУТ» — уже засеян (00001_init_schema.sql), общий механизм файлов. */
const SOUT_REPORTS_TYPE = 'sout_reports'

export function formatHistoricalDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

interface SoutDraft {
  hazardClass: string
  conductedOn: string
  comment: string
  /** Номер рабочего места по прошлой СОУТ (design D5, §1.1/§3 ТЗ) — необязательное. */
  rmNumber: string
  /**
   * Чекбокс «Декларация» (centrika-docs!29): по умолчанию не выбран, доступен только для
   * классов «1»/«2»; смена класса на другой сбрасывает его (см. `setHazardClass`).
   */
  declaration: boolean
}

function emptySoutDraft(): SoutDraft {
  return { hazardClass: '', conductedOn: '', comment: '', rmNumber: '', declaration: false }
}

/** Короткое название записи для диалога удаления: «Класс 2 · 01.06.2024». */
function recordTitle(item: SoutHistoricalResult): string {
  return `Класс ${item.hazardClass} · ${formatHistoricalDate(item.conductedOn)}`
}

export interface HistoricalSoutPanelProps {
  workplaceId: string
  /** Загрузка документа доступна только команде (design D7). */
  canUploadDocument?: boolean
  /**
   * Текст кнопки-переключателя в закрытом состоянии (design D5): точки входа из карточки
   * сотрудника/рабочего места (p3-structure-sout-training §1.1/§3) требуют текст «Добавить
   * историческую СОУТ» — прочие точки входа (дашборд «Спецоценка», заказ) сохраняют прежний
   * текст по умолчанию.
   */
  openLabel?: string
  /** Текст кнопки в открытом состоянии («Скрыть …», по умолчанию — прежний текст). */
  closeLabel?: string
  /**
   * Записи видны сразу, без раскрытия панели (карточки сотрудника и рабочего места): список
   * исторических записей с «Править»/«Удалить» загружается при открытии карточки, а кнопка
   * `openLabel` открывает форму новой записи. Без признака — прежняя сворачиваемая панель
   * (заказ: по панели на строку таблицы, список грузится по раскрытию).
   */
  inlineRecords?: boolean
  /**
   * Запись сохранена (создана, отредактирована или удалена) — колбэк для вызывающей стороны, ЧТОБЫ
   * перезагрузить соседние блоки, которые сами не подписаны на этот стор (сводка «Условия труда»
   * карточки сотрудника/«Хронология СОУТ» карточки места, code-review волны 2). Панель всегда
   * перезагружает СВОЙ список сама (`load()`) — этот колбэк дополнительный, необязательный;
   * без него поведение прежних точек входа (team-sout-section, заказ) не меняется.
   */
  onSaved?: () => void
}

export function HistoricalSoutPanel({
  workplaceId,
  canUploadDocument = false,
  openLabel = 'Историческая СОУТ',
  closeLabel = 'Скрыть историческую СОУТ',
  inlineRecords = false,
  onSaved,
}: HistoricalSoutPanelProps) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<SoutHistoricalResult[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<SoutDraft>(emptySoutDraft())
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<SoutHistoricalResult | null>(null)
  const [deleting, setDeleting] = useState(false)

  function load() {
    setLoading(true)
    setLoadError(null)
    soutApi.historical
      .list(workplaceId)
      .then(setItems)
      .catch((err) => setLoadError(getErrorMessage(err, 'Не удалось загрузить историю СОУТ')))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (open || inlineRecords) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inlineRecords, workplaceId])

  function startCreate() {
    setEditingId('new')
    setDraft(emptySoutDraft())
    setFile(null)
    setSaveError(null)
  }

  function startEdit(item: SoutHistoricalResult) {
    setEditingId(item.id)
    setDraft({
      hazardClass: item.hazardClass,
      conductedOn: item.conductedOn ?? '',
      comment: item.comment ?? '',
      rmNumber: item.rmNumber ?? '',
      // «Декларация» выбрана, если для записи класса «1»/«2» сохранён бессрочный срок
      // (centrika-docs!29). Сервер присылает готовый признак; при его отсутствии
      // (ответ старого формата) выводим из сохранённых класса и бессрочности.
      declaration: item.declaration ?? historicalDeclarationChecked(item),
    })
    setFile(null)
    setSaveError(null)
  }

  /**
   * Смена класса условий труда: класс, не допускающий декларацию («3.1»…«4»), сбрасывает
   * чекбокс в невыбранное состояние — срок пересчитывается как дата проведения + 5 лет
   * (centrika-docs!29).
   */
  function setHazardClass(hazardClass: string) {
    setDraft((d) => ({
      ...d,
      hazardClass,
      declaration: historicalDeclarationAllowed(hazardClass) ? d.declaration : false,
    }))
  }

  async function save() {
    setSaveError(null)
    setSaving(true)
    try {
      let documentFileId: string | undefined
      if (file && canUploadDocument) {
        const uploaded = await teamApi.uploadFile({ file, documentTypeCode: SOUT_REPORTS_TYPE })
        documentFileId = uploaded.id
      }
      const input = {
        workplaceId,
        hazardClass: draft.hazardClass,
        conductedOn: draft.conductedOn,
        comment: draft.comment.trim() || undefined,
        documentFileId,
        rmNumber: draft.rmNumber.trim() || undefined,
        // Признак декларации шлём всегда (в т.ч. false — снятие чекбокса при правке);
        // срок действия по нему считает сервер (centrika-docs!29).
        declaration: draft.declaration,
      }
      if (editingId && editingId !== 'new') {
        await soutApi.historical.update(editingId, input)
      } else {
        await soutApi.historical.create(input)
      }
      setEditingId(null)
      load()
      onSaved?.()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить историческую запись'))
    } finally {
      setSaving(false)
    }
  }

  /** Удаление необратимо — выполняется только после подтверждения в диалоге. */
  async function confirmAndRemove() {
    if (!confirmDelete) return
    const id = confirmDelete.id
    setSaveError(null)
    setDeleting(true)
    try {
      await soutApi.historical.remove(id)
      if (editingId === id) setEditingId(null)
      load()
      onSaved?.()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось удалить запись'))
    } finally {
      setDeleting(false)
      setConfirmDelete(null)
    }
  }

  const preview = historicalValidUntilPreview(draft.hazardClass, draft.conductedOn, draft.declaration)
  const previewText =
    preview.kind === 'perpetual' ? 'бессрочно' : preview.kind === 'date' ? formatHistoricalDate(preview.date) : '—'
  const declarationAllowed = historicalDeclarationAllowed(draft.hazardClass)

  const alerts = (
    <>
      {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
      {saveError ? <Alert variant="danger">{saveError}</Alert> : null}
    </>
  )

  const list = (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-8 border border-line p-2 text-13"
        >
          <span>
            Класс {item.hazardClass} · {formatHistoricalDate(item.conductedOn)} ·{' '}
            {item.perpetual ? 'бессрочно' : formatHistoricalDate(item.validUntil)}
            {item.rmNumber ? ` · РМ №${item.rmNumber}` : ''}
            {item.comment ? ` · ${item.comment}` : ''}
            {item.documentUrl ? (
              <>
                {' · '}
                <a className="text-accent-txt underline" href={item.documentUrl} download>
                  документ
                </a>
              </>
            ) : null}
          </span>
          <span className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => startEdit(item)}>
              Править
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(item)}>
              Удалить
            </Button>
          </span>
        </li>
      ))}
    </ul>
  )

  const form = (
    <div className="mt-3 flex flex-col">
      <Field label="Класс условий труда" required>
        {(props) => (
          <Select value={draft.hazardClass} onValueChange={setHazardClass}>
            <SelectTrigger
              id={props.id}
              aria-label="Класс условий труда"
              aria-required={props['aria-required']}
            >
              <SelectValue placeholder="Выберите класс" />
            </SelectTrigger>
            <SelectContent>
              {HAZARD_CLASSES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Дата проведения" required>
        {(props) => (
          <DateInput
            {...props}
            value={draft.conductedOn}
            onChange={(conductedOn) => setDraft((d) => ({ ...d, conductedOn }))}
          />
        )}
      </Field>
      <Field label="Номер рабочего места по прошлой СОУТ" hint="Необязательно">
        {(props) => (
          <Input
            {...props}
            value={draft.rmNumber}
            placeholder="Необязательно"
            onChange={(e) => setDraft((d) => ({ ...d, rmNumber: e.target.value }))}
          />
        )}
      </Field>
      {/* Строка срока действия (centrika-docs!29): вычисляемое поле «Действует
          до» и чекбокс «Декларация» РЯДОМ с ним. Чекбокс доступен только для
          классов «1»/«2»; для остальных — недоступен и не выбран. */}
      <div className="flex flex-wrap items-center gap-4">
        <Field
          className="min-w-48 flex-1"
          label="Действует до"
          hint="Вычисляется автоматически — не вводится вручную"
        >
          {(props) => <Input {...props} readOnly disabled value={previewText} />}
        </Field>
        <LabeledCheckbox
          checked={draft.declaration}
          disabled={!declarationAllowed}
          onCheckedChange={(declaration) => setDraft((d) => ({ ...d, declaration }))}
        >
          Декларация
        </LabeledCheckbox>
      </div>
      <Field label="Комментарий">
        {(props) => (
          <Input
            {...props}
            value={draft.comment}
            placeholder="Необязательно"
            onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
          />
        )}
      </Field>
      {canUploadDocument ? (
        <Field label="Документ-отчёт (необязательно)">
          {(props) => (
            <FileInput {...props} value={file} onChange={setFile} disabled={saving} />
          )}
        </Field>
      ) : null}
      <div className="flex gap-2">
        <Button disabled={saving || !draft.hazardClass || !draft.conductedOn} onClick={() => void save()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
        <Button variant="secondary" onClick={() => setEditingId(null)}>
          Отмена
        </Button>
      </div>
    </div>
  )

  const confirmDialog = (
    <Dialog
      open={confirmDelete !== null}
      onOpenChange={(next) => {
        if (!next) setConfirmDelete(null)
      }}
    >
      {confirmDelete ? (
        <DialogContent
          title="Удалить историческую СОУТ?"
          description={`Запись «${recordTitle(confirmDelete)}» будет удалена.`}
          className="max-w-sm"
        >
          <Card header="Удалить историческую СОУТ?">
            <p className="text-txt-secondary">
              Запись «{recordTitle(confirmDelete)}» будет удалена, сведения по СОУТ рабочего места
              пересчитаются. Действие необратимо.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                size="sm"
                className="border-red bg-red text-white hover:opacity-90"
                disabled={deleting}
                onClick={() => void confirmAndRemove()}
                leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
              >
                Удалить
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(null)}>
                Отмена
              </Button>
            </div>
          </Card>
        </DialogContent>
      ) : null}
    </Dialog>
  )

  if (inlineRecords) {
    return (
      <div className="mt-2">
        {alerts}
        {items.length > 0 ? (
          <div className="mb-2">
            <p className="py-1 text-13 text-txt-secondary">Исторические записи (внесены вручную)</p>
            {list}
          </div>
        ) : null}
        {editingId ? (
          form
        ) : (
          <Button size="sm" variant="secondary" onClick={startCreate}>
            {openLabel}
          </Button>
        )}
        {confirmDialog}
      </div>
    )
  }

  return (
    <div className="mt-2">
      <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
        {open ? closeLabel : openLabel}
      </Button>
      {open ? (
        <Card header="Историческая СОУТ (вне заказа)" className="mt-2">
          {alerts}
          {loading ? (
            <p className="py-2 text-txt-secondary">Загрузка…</p>
          ) : (
            <>
              {items.length === 0 ? (
                <p className="py-2 text-txt-secondary">Исторических записей пока нет.</p>
              ) : (
                list
              )}
              {editingId ? (
                form
              ) : (
                <Button className="mt-3" size="sm" onClick={startCreate}>
                  Добавить историческую запись
                </Button>
              )}
            </>
          )}
        </Card>
      ) : null}
      {confirmDialog}
    </div>
  )
}
